import { __esDecorate, __runInitializers } from "tslib";
import { StringTag } from '#library/decorator.library.js';
import { isCallable, isNumber, isAbortSignal } from '#library/assertion.library.js';
import { TIMEOUT_MAX } from '#library/number.library.js';
/**
 * ## Aborter
 * Disposable AbortController implementing TC39 Disposable.
 * Automatically aborts the underlying signal upon scope exit via `using`.
 */
let Aborter = (() => {
    let _classDecorators = [StringTag('Aborter')];
    let _classDescriptor;
    let _classExtraInitializers = [];
    let _classThis;
    let _classSuper = AbortController;
    var Aborter = class extends _classSuper {
        static { _classThis = this; }
        static {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(_classSuper[Symbol.metadata] ?? null) : void 0;
            __esDecorate(null, _classDescriptor = { value: _classThis }, _classDecorators, { kind: "class", name: _classThis.name, metadata: _metadata }, null, _classExtraInitializers);
            Aborter = _classThis = _classDescriptor.value;
            if (_metadata) Object.defineProperty(_classThis, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
            __runInitializers(_classThis, _classExtraInitializers);
        }
        /**
         * Creates a new disposable Aborter controller.
         */
        static create() {
            return new Aborter();
        }
        /**
         * TC39 explicit resource management hook.
         * Automatically aborts the controller when exiting scope via `using`.
         */
        [Symbol.dispose]() {
            if (!this.signal.aborted) {
                this.abort();
            }
        }
    };
    return Aborter = _classThis;
})();
export { Aborter };
/** Immutable shared noop function for null-object teardown patterns */
export const NOOP = Object.freeze(() => { });
/**
 * Safely binds an abort callback to an AbortSignal.
 * - If the signal is already aborted, invokes the callback immediately.
 * - If a signal is provided, attaches an event listener with `{ once: true }`.
 * - Returns a cleanup unbind function to safely detach the listener early (preventing memory leaks).
 *
 * @param signal - The AbortSignal to observe (optional)
 * @param callback - The callback to execute upon abort
 * @returns A teardown function that detaches the abort listener
 */
export function onAbort(signal, callback) {
    if (!signal)
        return NOOP;
    if (signal.aborted) {
        if (isCallable(callback))
            callback();
        return NOOP;
    }
    const listener = () => {
        if (isCallable(callback))
            callback();
    };
    signal.addEventListener('abort', listener, { once: true });
    return () => signal.removeEventListener('abort', listener);
}
/**
 * Combines multiple AbortSignals into a single composite signal using a cross-realm and duck-type safe controller.
 * Automatically filters out undefined or non-signal values and propagates the abort reason.
 * The returned signal provides a `cleanup()` method to detach listeners from parent signals early.
 *
 * @param signals - Array of AbortSignals (or undefined values)
 * @returns A composite AbortSignal with a cleanup teardown method
 */
export function anySignal(...signals) {
    const valid = signals.filter(isAbortSignal);
    if (valid.length === 0)
        return new AbortController().signal;
    if (valid.length === 1)
        return valid[0];
    for (const sig of valid) {
        if (sig.aborted) {
            const controller = new AbortController();
            controller.abort(sig.reason);
            return controller.signal;
        }
    }
    const controller = new AbortController();
    const unbinds = [];
    const cleanup = () => {
        for (const unbind of unbinds)
            unbind();
        unbinds.length = 0;
    };
    const trigger = (reason) => {
        cleanup();
        if (!controller.signal.aborted)
            controller.abort(reason);
    };
    for (const sig of valid) {
        unbinds.push(onAbort(sig, () => trigger(sig.reason)));
    }
    const composite = controller.signal;
    composite.cleanup = cleanup;
    composite[Symbol.dispose] = cleanup;
    return composite;
}
/**
 * Creates an AbortSignal with a timeout in milliseconds, optionally composed with an existing parent signal.
 * The returned signal provides a `cleanup()` method to detach listeners from the parent signal early.
 *
 * @param ms - Timeout duration in milliseconds
 * @param parentSignal - Optional parent AbortSignal to combine with the timeout
 * @returns An AbortSignal that aborts when either the timeout expires or the parent signal aborts
 */
export function timeoutSignal(ms, parentSignal) {
    const timeout = (isNumber(ms) || ms === Infinity) && ms >= 0 ? Math.min(Math.round(ms), TIMEOUT_MAX) : 0;
    const timeoutSig = AbortSignal.timeout(timeout);
    if (!isAbortSignal(parentSignal))
        return timeoutSig;
    return anySignal(parentSignal, timeoutSig);
}
/**
 * Safely releases a composed signal or timeout signal by unbinding its listeners from any parent signals.
 *
 * @param signal - The AbortSignal to clean up
 */
export function cleanupSignal(signal) {
    if (!signal)
        return;
    if (isCallable(signal.cleanup)) {
        signal.cleanup();
    }
    else if (isCallable(signal[Symbol.dispose])) {
        signal[Symbol.dispose]();
    }
}
